import React, { useState, useContext } from 'react';
import { useAuth } from '../AuthContext';
import { generateAIImage } from '../gemini';
import { PageContext } from '../PageContext';
import { Page, PageContextType } from '../types';
import AIConsentModal from './AIConsentModal';

interface AIImageGeneratorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImageGenerated: (base64: string) => void;
}

const AIImageGeneratorModal: React.FC<AIImageGeneratorModalProps> = ({ isOpen, onClose, onImageGenerated }) => {
    const { userProfile, useAICredit } = useAuth();
    const pageContext = useContext(PageContext) as PageContextType | undefined;
    const [prompt, setPrompt] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showConsentModal, setShowConsentModal] = useState(false);

    const handleGenerate = async () => {
        if (!userProfile?.aiConsentEnabled) {
            setShowConsentModal(true);
            return;
        }

        if (!prompt.trim()) {
            alert("Please enter a description for the image.");
            return;
        }

        if (!userProfile || userProfile.aiCredits <= 0) {
            alert("You're out of AI Credits!");
            pageContext?.switchPage(Page.AIHome, "AI Creator Studio");
            onClose();
            return;
        }

        setIsLoading(true);
        try {
            const base64Image = await generateAIImage(prompt);
            const creditUsed = await useAICredit();
            if (creditUsed) {
                onImageGenerated(base64Image);
            } else {
                onImageGenerated(base64Image);
            }
        } catch (error: any) {
            console.error("Generation failed:", error);
            alert(`AI Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in" onClick={onClose}>
            <div 
                className="bg-gray-900 text-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col border border-white/10 overflow-hidden animate-modal-pop-in" 
                onClick={e => e.stopPropagation()}
            >
                <div className="p-6 bg-gradient-to-br from-purple-800 to-indigo-900 border-b border-white/10">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-yellow-300 text-xl shadow-inner">
                            <i className="ph-duotone ph-sparkle animate-pulse"></i>
                        </div>
                        <h2 className="text-2xl font-bold aurora-text">AI Creator Studio</h2>
                    </div>
                    <p className="text-sm text-gray-300">Turn your imagination into a cryptic puzzle. Consumes 1 AI Credit.</p>
                </div>

                <div className="p-6 space-y-5 bg-gray-900/50">
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label htmlFor="ai-prompt" className="block text-sm font-bold text-purple-300 uppercase tracking-wider">Image Description</label>
                            <span className="text-[10px] bg-white/5 px-2 py-0.5 rounded text-gray-400">Powered by Gemini 2.5</span>
                        </div>
                        <textarea
                            id="ai-prompt"
                            rows={4}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="e.g., A neon cyberpunk city in the rain with flying cars..."
                            className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 transition-all outline-none resize-none"
                            disabled={isLoading}
                        />
                    </div>
                    
                    <div className="flex items-center justify-between bg-white/5 p-3 rounded-xl border border-white/5">
                        <div className="flex items-center gap-2">
                            <i className="ph-duotone ph-brain text-purple-400 text-xl"></i>
                            <span className="text-sm font-medium text-gray-300">Your Credits</span>
                        </div>
                        <span className="font-bold text-purple-400">{userProfile?.aiCredits ?? 0} Available</span>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row-reverse gap-3 p-6 pt-2 border-t border-white/5">
                    <button 
                        onClick={handleGenerate} 
                        disabled={isLoading || !prompt.trim()} 
                        className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500 text-white font-extrabold py-4 px-6 rounded-xl disabled:opacity-50 shadow-xl transition-all active:scale-95 flex justify-center items-center gap-3"
                    >
                        {isLoading ? (
                            <>
                                <div className="spinner-white h-5 w-5"></div>
                                <span>Generating Art...</span>
                            </>
                        ) : (
                            <>
                                <i className="ph-duotone ph-paint-brush-broad text-xl"></i>
                                <span>Generate Masterpiece</span>
                            </>
                        )}
                    </button>
                    <button onClick={onClose} disabled={isLoading} className="sm:w-24 bg-white/5 border border-white/10 text-gray-300 font-bold py-4 px-4 rounded-xl hover:bg-white/10 transition-all">
                        Cancel
                    </button>
                </div>
                
                {isLoading && (
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex flex-col items-center justify-center">
                        <div className="w-16 h-16 relative">
                            <div className="absolute inset-0 rounded-full border-4 border-purple-500/20"></div>
                            <div className="absolute inset-0 rounded-full border-4 border-t-purple-500 animate-spin"></div>
                        </div>
                        <p className="mt-6 font-bold aurora-text animate-pulse">Dreaming of your pixels...</p>
                    </div>
                )}
            </div>

            <AIConsentModal 
                isOpen={showConsentModal} 
                onClose={() => setShowConsentModal(false)} 
                onConsentGranted={handleGenerate} 
            />
        </div>
    );
};

export default AIImageGeneratorModal;